// Projects shared with this account (W9a): Leave Project… (with a confirmation), the banner on a project its owner
// removed this account from (or deleted), whose copy here still reads and exports, and the Library's list of shares
// waiting for their owners to confirm, each with the safety code to compare.
import { useEffect, useState } from 'react';
import { leaveShare } from '@writing-suite/client/net/shares.ts';
import { codeWith } from '@writing-suite/client/net/sharing.ts';
import { announce, useApp } from '../shell/context.ts';
import { navigate } from '../shell/route.ts';
import { projectTitle, type LibraryProject, type PendingShare } from '../store/library.ts';
import { ROLE_NAMES } from '../store/sharing.ts';
import { ConfirmDialog } from './Menu.tsx';
import { useExport, ExportStatus } from './ExportPage.tsx';

/** Forgets a shared project on this computer: its cached copy and its place in the kept shares list. */
async function forgetShared(app: ReturnType<typeof useApp>, project: Pick<LibraryProject, 'id'> & { shared?: { owner: string; projectId: string } }) {
  await app.account.store.forget(project.id);
  const kept = (await app.account.store.shares())?.shares ?? [];
  const s = project.shared;
  if (s) await app.account.store.putShares(kept.filter((x) => !(x.owner === s.owner && x.projectId === s.projectId)));
}

export function LeaveDialog({ project, onClose }: { project: LibraryProject; onClose: () => void }) {
  const app = useApp();
  const [error, setError] = useState<string | null>(null);
  const owner = project.shared?.ownerName || 'its owner';
  const closed = project.shared && project.shared.status !== 'active';
  return (
    <>
      <ConfirmDialog
        title={`Leave “${projectTitle(project)}”?`}
        message={
          <>
            <p>
              {closed
                ? 'Its copy on this computer goes too. Export it first if you want to keep it.'
                : `You won’t be able to open it any more, and ${owner} would have to invite you again. Its copy on this computer goes too: export it first if you want to keep it.`}
            </p>
            {error ? (
              <p className="error" role="alert">
                {error}
              </p>
            ) : null}
          </>
        }
        confirm="Leave Project"
        onCancel={onClose}
        onConfirm={() => {
          void (async () => {
            try {
              const s = project.shared;
              // A share the owner already closed has nothing left on the relay to leave.
              if (s && s.status === 'active') await leaveShare(app.account.session.api, s.owner, s.projectId);
              await forgetShared(app, project);
              await app.refreshProjects();
              announce(`Left “${projectTitle(project)}”`);
              onClose();
              navigate('/');
            } catch (e) {
              setError(e instanceof Error ? e.message : String(e));
            }
          })();
        }}
      />
    </>
  );
}

/** Above a shared project the relay no longer gives this account: what happened, and Export. */
export function RemovedBanner({ project }: { project: LibraryProject }) {
  const exporting = useExport();
  const owner = project.shared?.ownerName || 'Its owner';
  const deleted = project.shared?.status === 'deleted';
  return (
    <div className="quota-banner removed-banner" role="alert" data-removed-banner={project.shared?.status ?? 'removed'}>
      <p>
        {deleted
          ? `${owner} deleted this project, so it’s no longer shared with you.`
          : `${owner} removed you from this project. You can’t see anything written in it from now on.`}{' '}
        The copy on this computer still reads: export it to keep it.
      </p>
      <p className="quota-banner-links">
        <button type="button" className="link-button" onClick={() => void exporting.run([project])} disabled={exporting.busy}>
          Export…
        </button>
      </p>
      <ExportStatus run={exporting} />
    </div>
  );
}

/** One accepted share waiting for its owner: who, the role, and the safety code to compare with them. */
function PendingRow({ share }: { share: PendingShare }) {
  const app = useApp();
  const [code, setCode] = useState<string | null>(null);
  useEffect(() => {
    let live = true;
    void app.account.session
      .exchangeKey()
      .then(async (own) => (own && share.ownerPublicKey ? codeWith(own, share.ownerPublicKey) : null))
      .then((c) => live && setCode(c))
      .catch(() => {});
    return () => {
      live = false;
    };
  }, [app.account.session, share.ownerPublicKey]);
  return (
    <li className="project-row pending-share" data-pending-share={share.id}>
      <p>
        <span className="project-title">Waiting for {share.ownerName || 'its owner'} to confirm</span>
        <span className="project-meta">
          {ROLE_NAMES[share.role]} · safety code <strong className="safety-code" data-safety-code>{code ?? '…'}</strong>
        </span>
      </p>
    </li>
  );
}

/** The Library's shares waiting to be confirmed (polled while there are any). */
export function PendingShares() {
  const app = useApp();
  const pending = app.pendingShares;
  useEffect(() => {
    if (!pending.length || !app.online) return;
    const t = setInterval(() => void app.refreshProjects(), 5000);
    return () => clearInterval(t);
  }, [pending.length, app.online]); // eslint-disable-line react-hooks/exhaustive-deps
  if (!pending.length) return null;
  return (
    <section className="series pending-shares" aria-labelledby="pending-shares">
      <h2 className="eyebrow-heading" id="pending-shares">
        <span className="eyebrow">Shared with you</span>
      </h2>
      <p className="hint">Read the safety code to the person who invited you: theirs should be the same.</p>
      <ul className="project-list">
        {pending.map((s) => (
          <PendingRow key={s.id} share={s} />
        ))}
      </ul>
    </section>
  );
}

/** "Shared by Anna · Co-writer" under a shared project's title in the Library. */
export function SharedBadge({ project }: { project: LibraryProject }) {
  const s = project.shared;
  if (!s) return null;
  return (
    <span className="shared-by" data-shared-by={s.owner} data-role={s.role} data-share-status={s.status}>
      Shared by {s.ownerName || 'someone'} <span className="role-badge">{ROLE_NAMES[s.role]}</span>
      {s.status !== 'active' ? <span className="role-badge closed">{s.status === 'deleted' ? 'Deleted' : 'Removed'}</span> : null}
    </span>
  );
}

